import { Button, Form, Input, Skeleton, Space } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import SettingsPageLayout from "../SettingsPageLayout";
import SettingsEditLink from "../SettingsEditLink";
import SettingsSection, { SettingsInfoField, formatSettingsPhone } from "../SettingsSection";

const emptySupportInfo = {
    hotlineNumber: "",
    whatsappNumber: "",
    email: "",
    facebook: "",
    instagram: "",
};

const SupportInfo = ({ companyData, initialLoading }) => {
    const { t } = useTranslation("settings");
    const toast = useToast();
    const { updateMyCompanyData } = useUser();
    const [form] = Form.useForm();
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        form.setFieldsValue({
            ...emptySupportInfo,
            ...(companyData?.supportInfo || {}),
        });
    }, [companyData?.supportInfo, form]);

    const savedSupportInfo = { ...emptySupportInfo, ...(companyData?.supportInfo || {}) };

    const renderLink = (url) =>
        url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#5D5DF6] hover:underline">
                {url}
            </a>
        ) : null;

    const handleCancel = () => {
        form.setFieldsValue({
            ...emptySupportInfo,
            ...(companyData?.supportInfo || {}),
        });
        setIsEditing(false);
    };

    const onFinish = async (values) => {
        const supportInfo = {
            hotlineNumber: values.hotlineNumber || "",
            whatsappNumber: values.whatsappNumber || "",
            email: values.email || "",
            facebook: values.facebook || "",
            instagram: values.instagram || "",
        };

        setIsSubmitting(true);
        try {
            const response = await apiClient.patch("/api/company/myCompany", { supportInfo });

            if (response?.data?.success) {
                updateMyCompanyData({ supportInfo });
                toast.success({
                    message: t("successfullyUpdatedInformation"),
                    description: t("supportInformation.saved"),
                });
                setIsEditing(false);
            } else {
                throw new Error("Failed to update support information");
            }
        } catch (error) {
            toast.error({
                message: t("failedToUpdateInformation"),
                description: error?.response?.data?.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <SettingsPageLayout
            title={t("supportInformation.title")}
            description={t("supportInformation.description")}
            actions={
                isEditing ? (
                    <Space>
                        <Button shape="round" onClick={handleCancel} disabled={isSubmitting}>
                            {t("cancel")}
                        </Button>
                        <Button type="primary" shape="round" loading={isSubmitting} onClick={() => form.submit()}>
                            {isSubmitting ? t("saving") : t("save")}
                        </Button>
                    </Space>
                ) : initialLoading ? (
                    <Skeleton.Button style={{ width: "132px" }} />
                ) : (
                    <SettingsEditLink onClick={() => setIsEditing(true)} />
                )
            }>
            <SettingsSection bodyClassName={isEditing ? "!pb-1" : ""}>
                {initialLoading ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                    // The form stays mounted in read mode so its store keeps the values seeded above.
                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={customizeRequiredMark}
                        onFinish={onFinish}
                        disabled={!isEditing}>
                        {isEditing ? (
                            <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                                <Form.Item name="hotlineNumber" label={t("supportInformation.hotlineNumber")}>
                                    <PhoneInput country="us" enableSearch disabled={!isEditing} />
                                </Form.Item>
                                <Form.Item name="whatsappNumber" label={t("supportInformation.whatsappNumber")}>
                                    <PhoneInput country="us" enableSearch disabled={!isEditing} />
                                </Form.Item>
                                <Form.Item
                                    name="email"
                                    label={t("supportInformation.email")}
                                    rules={[{ type: "email", message: t("supportInformation.emailInvalid") }]}
                                    className="md:col-span-2">
                                    <Input placeholder={t("supportInformation.emailPlaceholder")} />
                                </Form.Item>
                                <Form.Item name="facebook" label={t("supportInformation.facebook")}>
                                    <Input placeholder={t("supportInformation.facebookPlaceholder")} />
                                </Form.Item>
                                <Form.Item name="instagram" label={t("supportInformation.instagram")}>
                                    <Input placeholder={t("supportInformation.instagramPlaceholder")} />
                                </Form.Item>
                            </div>
                        ) : (
                            <dl className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
                                <SettingsInfoField label={t("supportInformation.hotlineNumber")}>
                                    {formatSettingsPhone(savedSupportInfo.hotlineNumber)}
                                </SettingsInfoField>
                                <SettingsInfoField label={t("supportInformation.whatsappNumber")}>
                                    {formatSettingsPhone(savedSupportInfo.whatsappNumber)}
                                </SettingsInfoField>
                                <SettingsInfoField label={t("supportInformation.email")} className="md:col-span-2">
                                    {savedSupportInfo.email}
                                </SettingsInfoField>
                                <SettingsInfoField label={t("supportInformation.facebook")}>
                                    {renderLink(savedSupportInfo.facebook)}
                                </SettingsInfoField>
                                <SettingsInfoField label={t("supportInformation.instagram")}>
                                    {renderLink(savedSupportInfo.instagram)}
                                </SettingsInfoField>
                            </dl>
                        )}
                    </Form>
                )}
            </SettingsSection>
        </SettingsPageLayout>
    );
};

SupportInfo.propTypes = {
    companyData: PropTypes.object,
    initialLoading: PropTypes.bool,
};

export default SupportInfo;
